import React from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { useParams, useHistory } from 'react-router-dom'
import {
  Button,
  CircularProgress,
  Paper,
  Grid,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@material-ui/core'
import { Add, FilterList } from '@material-ui/icons'
import LineItemRow from './LineItemRow'
import BulkImportModal from './BulkImportModal'
import MatchSuggestionsPanel from './MatchSuggestionsPanel'
import BidEstimationHeader from '../shared/BidEstimationHeader'

const fmt = (v) => (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

const LineItemsView = (props) => {
  const { projectId } = useParams()
  const history = useHistory()
  const [project, setProject] = React.useState(null)
  const [lineItems, setLineItems] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [buildingFilter, setBuildingFilter] = React.useState('')
  const [sectionFilter, setSectionFilter] = React.useState('')
  const [coverageFilter, setCoverageFilter] = React.useState('')
  const [moduleFilter, setModuleFilter] = React.useState('')
  const [modules, setModules] = React.useState([])
  const [importOpen, setImportOpen] = React.useState(false)
  const [addOpen, setAddOpen] = React.useState(false)
  const [newItem, setNewItem] = React.useState({ description: '', unit: '', quantity: '', building: '', section: '' })

  React.useEffect(() => {
    fetchProject()
    fetchLineItems()
    // Fetch modules for filter dropdown
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}/modules`).then(res => {
      setModules(res.data || [])
    }).catch(() => {})
    // Pre-populate moduleFilter from URL param
    const urlModuleId = new URLSearchParams(window.location.search).get('moduleId')
    if (urlModuleId) setModuleFilter(urlModuleId)
  }, [projectId])

  const fetchProject = () => {
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}`).then(res => {
      setProject(res.data)
    }).catch(err => console.error(err))
  }

  const fetchLineItems = () => {
    setLoading(true)
    const params = {}
    if (buildingFilter) params.building = buildingFilter
    if (sectionFilter) params.section = sectionFilter
    if (coverageFilter) params.coverageStatus = coverageFilter
    if (moduleFilter) params.moduleId = moduleFilter
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}/line-items`, { params }).then(res => {
      setLineItems(res.data)
      setLoading(false)
    }).catch(err => {
      console.error(err)
      setLoading(false)
    })
  }

  React.useEffect(() => {
    if (project) fetchLineItems()
  }, [buildingFilter, sectionFilter, coverageFilter, moduleFilter])

  const sections = React.useMemo(() => {
    const unique = [...new Set(lineItems.map(i => i.section).filter(Boolean))]
    return unique.sort()
  }, [lineItems])

  const grouped = React.useMemo(() => {
    const map = {}
    lineItems.forEach(item => {
      const sec = item.section || 'Uncategorized'
      if (!map[sec]) map[sec] = []
      map[sec].push(item)
    })
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]))
  }, [lineItems])

  const summary = React.useMemo(() => {
    const total = lineItems.length
    const covered = lineItems.filter(i => i.coverageStatus === 'covered').length
    const missing = lineItems.filter(i => i.coverageStatus === 'missing').length
    const totalCost = lineItems.reduce((s, i) => s + (i.totalCost || 0), 0)
    const totalSell = lineItems.reduce((s, i) => s + (i.totalSell || 0), 0)
    return { total, covered, missing, totalCost, totalSell }
  }, [lineItems])

  const handleAddItem = () => {
    const building = newItem.building || (project?.buildings?.[0]?.name || '')
    if (!newItem.description.trim() || !building) return
    axios.post(`${props.url}/api/bid-estimation/projects/${projectId}/line-items`, {
      description: newItem.description.trim(),
      unit: newItem.unit.trim() || 'LS',
      quantity: parseFloat(newItem.quantity) || 0,
      building,
      section: newItem.section.trim()
    }).then(() => {
      setAddOpen(false)
      setNewItem({ description: '', unit: '', quantity: '', building: '', section: '' })
      fetchLineItems()
    }).catch(err => console.error(err))
  }

  return (
    <div style={{ padding: 20 }}>
      <BidEstimationHeader
        backTo={`/bid-estimation/${projectId}`}
        backLabel="Project"
        breadcrumbs={[
          { label: 'Bid Estimation', to: '/bid-estimation' },
          { label: project?.name || 'Project', to: `/bid-estimation/${projectId}` },
          { label: 'Line Items' }
        ]}
        title={`Line Items${project?.name ? ' · ' + project.name : ''}`}
        actions={(
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Add />}
              onClick={() => setImportOpen(true)}
              style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}
            >
              Import
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<Add />}
              onClick={() => setAddOpen(true)}
            >
              Add Item
            </Button>
          </>
        )}
      />

      {/* Filter bar */}
      <Paper style={{ padding: 12, marginBottom: 16 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <FilterList fontSize="small" style={{ marginTop: 6 }} />
          </Grid>
          <Grid item>
            <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
              <InputLabel>Module</InputLabel>
              <Select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} label="Module">
                <MenuItem value="">All Modules</MenuItem>
                {modules.map(m => (
                  <MenuItem key={m._id} value={m._id}>{m.scope}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
              <InputLabel>Building</InputLabel>
              <Select value={buildingFilter} onChange={e => setBuildingFilter(e.target.value)} label="Building">
                <MenuItem value="">All Buildings</MenuItem>
                {(project?.buildings || []).map(b => (
                  <MenuItem key={b.name} value={b.name}>{b.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
              <InputLabel>Section</InputLabel>
              <Select value={sectionFilter} onChange={e => setSectionFilter(e.target.value)} label="Section">
                <MenuItem value="">All Sections</MenuItem>
                {sections.map(s => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
              <InputLabel>Coverage</InputLabel>
              <Select value={coverageFilter} onChange={e => setCoverageFilter(e.target.value)} label="Coverage">
                <MenuItem value="">All</MenuItem>
                <MenuItem value="covered">Covered</MenuItem>
                <MenuItem value="partial">Partial</MenuItem>
                <MenuItem value="missing">Missing</MenuItem>
                <MenuItem value="needs_review">Needs Review</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary bar */}
      <Paper style={{ padding: 12, marginBottom: 16 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item><Typography variant="body2"><strong>Items:</strong> {summary.total}</Typography></Grid>
          <Grid item>
            <Chip size="small" label={`Covered: ${summary.covered}`} style={{ backgroundColor: '#c8e6c9', color: '#2e7d32' }} />
          </Grid>
          <Grid item>
            <Chip size="small" label={`Missing: ${summary.missing}`} style={{ backgroundColor: '#ffcdd2', color: '#c62828' }} />
          </Grid>
          <Grid item style={{ marginLeft: 'auto' }}>
            <Typography variant="body2"><strong>Total Cost:</strong> {fmt(summary.totalCost)}</Typography>
          </Grid>
          <Grid item>
            <Typography variant="body2"><strong>Total Sell:</strong> {fmt(summary.totalSell)}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Match suggestions — auto-hides when no matches */}
      <MatchSuggestionsPanel
        projectId={projectId}
        url={props.url}
        onMatchConfirmed={fetchLineItems}
      />

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <CircularProgress />
        </div>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 40 }} />
                <TableCell style={{ width: 60 }}>#</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Unit Cost ($)</TableCell>
                <TableCell align="right">Total Sell ($)</TableCell>
                <TableCell align="center">Coverage</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grouped.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" style={{ padding: 20 }}>
                      No line items yet. Upload plans, import a bid response, or click "Add Item" to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                grouped.map(([section, items]) => (
                  <React.Fragment key={section}>
                    <TableRow>
                      <TableCell colSpan={9} style={{ backgroundColor: '#f5f5f5', fontWeight: 600 }}>
                        <Typography variant="subtitle2">{section}</Typography>
                      </TableCell>
                    </TableRow>
                    {items.map(item => (
                      <LineItemRow
                        key={item._id}
                        item={item}
                        project={project}
                        url={props.url}
                        onRefresh={fetchLineItems}
                      />
                    ))}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <BulkImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={() => { setImportOpen(false); fetchLineItems() }}
        project={project}
        url={props.url}
      />

      {/* Add single item dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Line Item</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal" variant="outlined" size="small">
            <InputLabel>Building</InputLabel>
            <Select
              value={newItem.building}
              onChange={e => setNewItem({ ...newItem, building: e.target.value })}
              label="Building"
            >
              {(project?.buildings || []).map(b => (
                <MenuItem key={b.name} value={b.name}>{b.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Section" value={newItem.section} onChange={e => setNewItem({ ...newItem, section: e.target.value })} fullWidth margin="normal" variant="outlined" size="small" />
          <TextField label="Description" value={newItem.description} onChange={e => setNewItem({ ...newItem, description: e.target.value })} fullWidth margin="normal" variant="outlined" size="small" required />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField label="Unit" value={newItem.unit} onChange={e => setNewItem({ ...newItem, unit: e.target.value })} fullWidth margin="normal" variant="outlined" size="small" placeholder="e.g. SF, LF, EA" />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Quantity" value={newItem.quantity} onChange={e => setNewItem({ ...newItem, quantity: e.target.value })} fullWidth margin="normal" variant="outlined" size="small" type="number" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button onClick={handleAddItem} variant="contained" color="primary" disabled={!newItem.description.trim()}>Add</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  authenticated: state.authenticated
})

export default connect(mapStateToProps)(LineItemsView)
